// 가방 창(가로 화면) 왼쪽 겹층 배치: 카엘 입상과 장착 6칸(왼쪽 무기·투구·갑옷, 오른쪽 장갑·신발·목걸이)을 같은 기준 상자에 넣는다.
// 대표님 피드백(2026-10-27 "가운데 있어야 할 캐릭터가 좌측에 쏠려 있다"): 예전에는 입상 left:300px 고정, 칸 상자 left:142px·폭 420px(가운데 352px)로
// 기준이 달라 입상이 왼쪽 열에 겹쳤다. 이제 두 열의 가운데(dollCenter)에 입상 몸통 중심을 맞추고, 상자는 능력치 상자 오른쪽 ~ 오른쪽 패널 왼쪽 사이에서 가운데 둔다.
// 좌표는 rpg.ts 가로 창의 설계 좌표(1280×720 기준 zoom 전 px). 순수 함수(DOM 없음) — 시험이 창 크기마다 가운데·겹침을 고정한다.
import type { GearTier } from '../shared/rpg';

/** 오른쪽 패널(.dh-r-panel right:20px; width:600px) */
export const BAG_PANEL = { right: 20, width: 600 } as const;
/** 왼쪽 아래 능력치 상자(.dh-r-sbox left:12px; width:216px; bottom:16px) */
export const BAG_STATBOX = { left: 12, width: 216, bottom: 16 } as const;
/** 장착 칸 열 폭(.dh-r-gdoll grid 78px 1fr 78px) */
export const BAG_COL = 78;
/** 입상 원화 611×900 */
export const KAEL_ART = { w: 611, h: 900 } as const;
/**
 * 입상 그림 안 몸통 중심(가로 비율). 알파 30~60% 높이 띠의 무게중심(tools로 잰 값, 망토가 한쪽으로 퍼져 그림 가운데와 다르다).
 * 이 비율로 몸통 중심을 상자 가운데에 맞춘다. 보이는 영역(알파 경계) 왼쪽 끝 비율도 함께 둔다(능력치 상자 겹침 판정).
 */
export const KAEL_BODY: Readonly<Record<GearTier, { torso: number; left: number; right: number }>> = {
  1: { torso: 0.532, left: 142 / 611, right: 510 / 611 },
  2: { torso: 0.477, left: 30 / 611, right: 580 / 611 },
  3: { torso: 0.535, left: 7 / 611, right: 601 / 611 },
};

export interface BagDollLayout {
  /** 장착 칸 상자 왼쪽·폭·위(설계 px) */
  readonly left: number;
  readonly width: number;
  readonly top: number;
  /** 두 열 사이 가운데 x(= 입상 몸통 중심) */
  readonly center: number;
  /** 입상 높이(바닥에 붙는다) */
  readonly artH: number;
}

/** 상자 최대 폭: 너무 넓으면 두 열이 입상에서 멀어진다(844×390처럼 설계 폭이 1558px인 화면) */
const DOLL_MAX_W = 470;
/** 상자 최소 폭: 두 열 + 가운데 입상 몸통 */
const DOLL_MIN_W = BAG_COL * 2 + 160;
/** 능력치 상자·오른쪽 패널과 띄우는 간격 */
const GAP = 16;

/** dw·dh = 가로 창 설계 폭·높이(rpg.ts dialog width/height = 화면 / zoom) */
export function bagDollLayout(dw: number, dh: number): BagDollLayout {
  const l0 = BAG_STATBOX.left + BAG_STATBOX.width + GAP;
  const r0 = dw - BAG_PANEL.right - BAG_PANEL.width - GAP;
  const avail = Math.max(0, r0 - l0);
  const width = Math.max(DOLL_MIN_W, Math.min(avail, DOLL_MAX_W));
  const center = Math.round(l0 + avail / 2);
  const artH = Math.round(Math.max(420, Math.min(600, dh - 100)));
  // 칸 상자는 입상 어깨 높이에서 시작한다(높은 4:3 화면은 입상이 아래로 내려가므로 같이 내린다)
  const top = Math.round(Math.max(150, dh - artH + 40));
  return { left: Math.round(center - width / 2), width, top, center, artH };
}

/** 입상 img의 left(설계 px): 몸통 중심이 center에 오게 */
export function kaelArtLeft(center: number, artH: number, tier: GearTier): number {
  return center - KAEL_BODY[tier].torso * artH * (KAEL_ART.w / KAEL_ART.h);
}

/** 입상의 보이는 영역(알파 경계) x 범위 */
export function kaelVisibleX(center: number, artH: number, tier: GearTier): { x0: number; x1: number } {
  const w = artH * (KAEL_ART.w / KAEL_ART.h), l = kaelArtLeft(center, artH, tier);
  return { x0: l + KAEL_BODY[tier].left * w, x1: l + KAEL_BODY[tier].right * w };
}

/** rpg.ts가 CSS 변수로 쓰는 몸통 보정 계수(left = center − artH × k) */
export function kaelTorsoK(tier: GearTier): number {
  return KAEL_BODY[tier].torso * (KAEL_ART.w / KAEL_ART.h);
}
